import { unitCost } from './cost';
import { evaluate, fixedPartAt } from './fees';
import { cliffBelow, percentOfPriceBps, solvePrice } from './price';
import { roundFriendlyUp, roundHalfUp } from './round';
import type {
  ChannelInput,
  ChannelResult,
  ChannelWarning,
  PricingInput,
  PricingResult,
} from './types';

/** Stored in quote/order snapshots so a later engine change never rewrites history. */
export const PRICING_ENGINE_VERSION = '1.0.0';

function channelResult(input: PricingInput, unit: number, channel: ChannelInput): ChannelResult {
  const { rates } = input;
  const targetMarginBps = channel.targetMarginBps ?? rates.targetMarginBps;
  const opts = { taxBps: rates.taxBps, minimumMarginBps: rates.minimumMarginBps };

  const raw = solvePrice(unit, channel, rates.taxBps, targetMarginBps);
  const minimum = solvePrice(unit, channel, rates.taxBps, rates.minimumMarginBps);
  const breakEven = solvePrice(unit, channel, rates.taxBps, 0);

  let suggested: ChannelResult['suggested'] = null;
  if (raw !== null) {
    // Round to the centavo without leaving the fee range the price was solved in
    // (78,996 must not become 79,00 and enter free shipping).
    const rounded = roundHalfUp(raw);
    const rawCentavos =
      fixedPartAt(rounded, channel) === fixedPartAt(Math.floor(raw), channel)
        ? rounded
        : Math.floor(raw);
    const factor = 1 + (input.adjustments.rushBps - input.adjustments.discountBps) / 10_000;
    const adjustedCentavos = Math.max(0, roundHalfUp(rawCentavos * factor));
    suggested = {
      rawCentavos,
      adjustedCentavos,
      priceCentavos: roundFriendlyUp(adjustedCentavos, input.rounding),
    };
  }

  const override = channel.overridePriceCentavos ?? null;
  const evaluation =
    override !== null
      ? evaluate(override, 'override', unit, channel, opts)
      : suggested
        ? evaluate(suggested.priceCentavos, 'suggested', unit, channel, opts)
        : null;

  const warnings: ChannelWarning[] = [];
  if (evaluation) {
    const cliff = cliffBelow(evaluation.priceCentavos, channel, input.rounding);
    if (cliff && cliff.candidate < evaluation.priceCentavos) {
      const there = evaluate(cliff.candidate, 'suggested', unit, channel, opts);
      if (there.netProfitCentavos >= evaluation.netProfitCentavos) {
        warnings.push({
          code: 'fee_cliff',
          thresholdCentavos: cliff.threshold,
          freeShipping: cliff.freeShipping,
          suggestedPriceCentavos: cliff.candidate,
          netProfitCentavos: there.netProfitCentavos,
          marginBps: there.marginBps,
        });
      }
    }
    const orderTotal = evaluation.priceCentavos * Math.max(1, input.quantity);
    if (input.minimumOrderCentavos > 0 && orderTotal < input.minimumOrderCentavos) {
      warnings.push({
        code: 'below_minimum_order',
        minimumOrderCentavos: input.minimumOrderCentavos,
        orderTotalCentavos: orderTotal,
      });
    }
  }

  return {
    channelId: channel.id,
    name: channel.name,
    targetMarginBps,
    percentOfPriceBps: percentOfPriceBps(channel, rates.taxBps, targetMarginBps),
    status: raw === null ? 'impossible' : 'ok',
    suggested,
    minimumPriceCentavos: minimum === null ? null : Math.ceil(minimum - 1e-9),
    breakEvenPriceCentavos: breakEven === null ? null : Math.ceil(breakEven - 1e-9),
    evaluation,
    warnings,
  };
}

/** Unit cost and price per channel for one Quick Quote / product variant. Pure and deterministic. */
export function calculate(input: PricingInput): PricingResult {
  const base = unitCost(input);
  return {
    engineVersion: PRICING_ENGINE_VERSION,
    ...base,
    channels: input.channels.map((c) => channelResult(input, base.cost.unitCost, c)),
  };
}
